import React from 'react';

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      isChunkError: false 
    };
  }

  static getDerivedStateFromError(error) {
    // Check if this is a chunk loading error
    const isChunkError = error?.name === 'ChunkLoadError' || 
                        error?.message?.includes('Loading chunk') ||
                        error?.message?.includes('Unexpected token');
    
    return {
      hasError: true,
      error,
      isChunkError
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ChunkErrorBoundary caught an error:', error, errorInfo);
    
    // If it's a chunk error, log additional debugging info
    if (this.state.isChunkError) {
      console.error('Chunk loading error details:', {
        error: error.message,
        stack: error.stack,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, isChunkError: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        // Special UI for chunk loading errors
        return (
          <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-gray-100 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-gray-800/50 rounded-xl p-8 border border-gray-700/30 shadow-2xl backdrop-blur-sm">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-2">
                  Loading Issue
                </h3>
                
                <p className="text-gray-400 mb-6 text-sm">
                  The application failed to load some components. This might be due to a network issue or cached files.
                </p>
                
                <div className="space-y-3">
                  <button
                    onClick={this.handleReload}
                    className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Reload Page
                  </button>
                  
                  <button
                    onClick={this.handleRetry}
                    className="w-full px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                  >
                    Try Again
                  </button>
                </div>
                
                <div className="mt-4 text-xs text-gray-500">
                  If this problem persists, try clearing your browser cache.
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Generic error UI for other errors
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-gray-100 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-gray-800/50 rounded-xl p-8 border border-gray-700/30 shadow-2xl backdrop-blur-sm">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <h3 className="text-xl font-semibold text-white mb-2">
                Something went wrong
              </h3>
              
              <p className="text-gray-400 mb-6 text-sm">
                An unexpected error occurred. Please try reloading the page.
              </p>
              
              <button
                onClick={this.handleReload}
                className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
